import { useState } from "react";
import { toast } from "sonner";
import { Linkedin, Twitter, Instagram, Youtube, ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

const COLS = [
  { title: "Services", links: ["SEO", "Google Ads", "Meta Ads", "Web Development", "AI Chatbots", "Funnel Building"] },
  { title: "Company", links: ["About", "Case Studies", "Process", "Careers", "Press"] },
  { title: "Resources", links: ["Insights", "Playbooks", "AI Glossary", "ROI Calculator", "Contact"] },
];

const SOCIAL_DEFS = [
  { key: "linkedin", Icon: Linkedin },
  { key: "twitter", Icon: Twitter },
  { key: "instagram", Icon: Instagram },
  { key: "youtube", Icon: Youtube },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const { config } = useSiteConfig();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid email to subscribe.");
      return;
    }
    toast.success("Subscribed. Welcome to the Quantum Star desk.");
    setEmail("");
  };

  const activeSocial = SOCIAL_DEFS.filter(({ key }) => (config.social[key] || "").trim() !== "");

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/5 pt-20 pb-10 overflow-hidden bg-[#050505]">
      <div className="aurora-soft opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-white/5">
          {/* Brand + newsletter + contact */}
          <div className="lg:col-span-5">
            <a href="#top" className="inline-flex items-center gap-2">
              <img
                src="/logo.svg"
                alt="Quantum Star Marketing logo"
                className="h-9 w-9 drop-shadow-[0_0_20px_rgba(157,76,221,0.55)]"
              />
              <span className="font-clash text-2xl font-semibold tracking-tight">
                Quantum Star<span className="text-cyan-400"> Marketing</span>
              </span>
            </a>
            <p className="mt-5 max-w-md text-sm text-zinc-400 leading-relaxed">
              {config.brand.tagline} We engineer compounding revenue with performance marketing and AI automation.
            </p>

            <form onSubmit={subscribe} className="mt-7 flex items-center gap-2 rounded-full glass p-1.5 max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Subscribe to the Quantum desk"
                className="flex-1 bg-transparent px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full bg-white text-black px-5 py-2.5 text-xs font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
                data-testid="newsletter-submit"
              >
                Subscribe
              </button>
            </form>

            <div className="mt-6 space-y-2 text-sm text-zinc-400">
              {config.contact.email && (
                <a href={`mailto:${config.contact.email}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="contact-email">
                  <Mail size={13} className="text-cyan-300" /> {config.contact.email}
                </a>
              )}
              {config.contact.phone && (
                <a href={`tel:${config.contact.phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="contact-phone">
                  <Phone size={13} className="text-cyan-300" /> {config.contact.phone}
                </a>
              )}
              {config.contact.address && (
                <div className="flex items-start gap-2" data-testid="contact-address">
                  <MapPin size={13} className="text-cyan-300 mt-1" /> {config.contact.address}
                </div>
              )}
            </div>

            {activeSocial.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                {activeSocial.map(({ key, Icon }, i) => (
                  <a
                    key={key}
                    href={config.social[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                    data-testid={`social-link-${key}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full glass hover:bg-white/10 hover:border-purple-400/30 transition-colors"
                  >
                    <Icon size={15} className="text-zinc-300" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-10">
            {COLS.map((c) => (
              <div key={c.title}>
                <div className="text-[10px] font-mono-acc uppercase tracking-[0.25em] text-zinc-500 mb-5">{c.title}</div>
                <ul className="space-y-3">
                  {c.links.map((l) => (
                    <li key={l}>
                      <a href="#" className="text-sm text-zinc-300 hover:text-white transition-colors inline-flex items-center gap-1.5 group">
                        {l}
                        <ArrowUpRight size={12} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-cyan-400" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Massive brand */}
        <div className="py-14 text-center select-none">
          <h3 className="font-clash text-[14vw] md:text-[11vw] lg:text-[9.5rem] font-semibold leading-[0.9] tracking-tighter text-gradient">
            QUANTUM STAR
          </h3>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
          <div className="text-xs text-zinc-500 font-mono-acc tracking-wide">
            © {new Date().getFullYear()} {config.legal.copyrightOwner} — Built for ambitious brands.
          </div>
          <div className="flex items-center gap-6 text-xs text-zinc-500">
            <a href="#" className="hover:text-zinc-300 transition-colors">Privacy</a>
            <a href="#" className="hover:text-zinc-300 transition-colors">Terms</a>
            <a href="#" className="hover:text-zinc-300 transition-colors">Security</a>
            <div className="hidden md:flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
              All systems operational
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
